import React from "react";
import useHubspotEmbed from "@/hooks/forms/use-hubspot-embed";
import {HubSpotFormInfo} from "@/types/dto/common/hubspot";
import {Box, Stack, Typography} from "@mui/material";
import HubspotEmbed from "@/components/forms/hubspot-embed";
import HubspotFormDisclaimer from "@/components/forms/disclaimer/hubspot-form-disclaimer";
import {
  isDarkLandingPageBackground,
  resolveLandingPageSectionBackgroundColor
} from "@/utils/landing-page-background";

interface HubspotShellProps {
  headerText: string;
  copyText?: string;
  form: HubSpotFormInfo;
  onSubmit?: () => void;
  backgroundColor?: string;
  moduleId?: string;
}

const HubspotFormShell = ({
  headerText,
  copyText,
  form,
  onSubmit,
  backgroundColor,
  moduleId
}: HubspotShellProps) => {
  const resolvedBackgroundToken = backgroundColor
    ? resolveLandingPageSectionBackgroundColor(backgroundColor)
    : undefined;

  const formBackgroundTokenMap: Record<string, string> = {
    "background.default": "background.warm_strong",
    "background.cool": "background.default",
    "background.cool_strong": "background.default",
    "background.warm": "background.default",
    "background.warm_strong": "background.default",
    "background.brand": "background.default",
    "background.brand_subtle": "background.default",
    transparent: "background.warm_strong"
  };

  const bgColorMap: Record<string, string> = {
    "background.default": "var(--theme-palette-background-default)",
    "background.cool": "var(--theme-palette-background-cool)",
    "background.cool_strong": "var(--theme-palette-background-cool_strong)",
    "background.warm": "var(--theme-palette-background-warm)",
    "background.warm_strong": "var(--theme-palette-background-warm_strong)",
    "background.brand": "var(--theme-palette-background-brand)",
    "background.brand_subtle": "var(--theme-palette-background-brand_subtle)",
    transparent: "transparent"
  };

  const shellClassMap: Record<string, string> = {
    "background.default": "hubspot-shell-white",
    "background.warm": "hubspot-shell-gray",
    "background.warm_strong": "hubspot-shell-gray",
    "background.cool": "hubspot-shell-light-blue",
    "background.cool_strong": "hubspot-shell-light-blue",
    "background.brand": "hubspot-shell-blue",
    "background.brand_subtle": "hubspot-shell-blue",
    transparent: "hubspot-shell-transparent"
  };

  const resolvedFormBackgroundToken = resolvedBackgroundToken
    ? formBackgroundTokenMap[resolvedBackgroundToken] || resolvedBackgroundToken
    : undefined;
  const resolvedBgColor = resolvedFormBackgroundToken
    ? bgColorMap[resolvedFormBackgroundToken] || "transparent"
    : "transparent";
  const resolvedTextColor = isDarkLandingPageBackground(resolvedFormBackgroundToken)
    ? "var(--theme-palette-common-white)"
    : "var(--theme-palette-text-primary)";
  const shellClassName = resolvedFormBackgroundToken
    ? shellClassMap[resolvedFormBackgroundToken] || ""
    : "";

  const {
    formId,
    formName,
    portalId
  } = form;
  const targetId = moduleId ? `hubspotForm-${formId}-${moduleId}` : undefined;
  const embedProps = {
    formId,
    portalId,
    formName,
    onSubmit,
    ...(targetId ? { targetId } : {})
  };

  const {
    dataLayerProps,
    isLoading,
    isSubmitted,
    targetId: resolvedTargetId
  } = useHubspotEmbed(embedProps);

  return (
    <Stack
      className={shellClassName}
      sx={{
        backgroundColor: resolvedBgColor,
        padding: "4rem",
        gap: "2.4rem",
        borderRadius: "2rem",
        alignItems: "center",
        width: "fit-content",
        maxWidth: "82rem !important",
        boxShadow: resolvedBgColor !== "transparent" ? 6 : 0
      }}
      id={`hubspot-form-shell-${formId}`}
    >
      <Typography
        component="h2"
        variant="h3"
        data-testid={`${formId}-hubspot-shell-header`}
        sx={{
          color: resolvedTextColor,
          maxWidth: "70rem",
          textAlign: "center"
        }}
      >
        {headerText}
      </Typography>
      {copyText && (
        <Box
          data-testid={`${formId}-hubspot-shell-copy`}
          sx={{
            color: resolvedTextColor,
            marginBottom: "2rem",
            textAlign: "center",
            "& p": { margin: 0 },
            maxWidth: "70rem"
          }}
          dangerouslySetInnerHTML={{ __html: copyText }}
        />
      )}
      <Box
        sx={{
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.5s ease-in"
        }}
      >
        <HubspotEmbed
          formId={formId}
          dataLayerProps={dataLayerProps}
          targetId={resolvedTargetId ?? targetId}
          className=""
        />
      </Box>
      {!isSubmitted && (
        <HubspotFormDisclaimer
          testId={`${formId}-hubspot-shell-form-disclaimer`}
          sx={{
            color: resolvedTextColor,
            padding: 0
          }}
        />
      )}
    </Stack>
  );
};

export default HubspotFormShell;